/**
 * 批量事务抽屉 V4.0 - 状态管理 Reducer
 * 基于客户端状态追踪模式的统一状态管理
 *
 * 重构说明：
 * 1. 使用工具函数替代内联辅助函数
 * 2. 从常量文件导入常量
 * 3. 保持现有功能不变
 */

import { CLIENT_STATUS, TRANSACTION_STATUS, DATA_LOADING_STATUS, WORKFLOW_MODE } from "../types";
import {
    extractAirportsFromFlights,
    generateTempId,
    findTransactionById,
    findDetailById
} from "../utils";

// Action 类型定义
export const ActionTypes = {
    // 初始化和重置
    INITIALIZE_STATE: "INITIALIZE_STATE",
    RESET_STATE: "RESET_STATE",

    // 数据加载
    SET_LOADING: "SET_LOADING",
    SET_DATA_LOADING_STATUS: "SET_DATA_LOADING_STATUS",

    // 基础数据设置
    SET_SELECTED_FLIGHTS: "SET_SELECTED_FLIGHTS",
    SET_TRANSACTION_TYPE: "SET_TRANSACTION_TYPE",
    SET_WORKFLOW_MODE: "SET_WORKFLOW_MODE",

    // 机场和供应商
    SET_AVAILABLE_AIRPORTS: "SET_AVAILABLE_AIRPORTS",
    SET_ACTIVE_AIRPORTS: "SET_ACTIVE_AIRPORTS",
    SET_AVAILABLE_SUPPLIERS: "SET_AVAILABLE_SUPPLIERS",
    SET_SELECTED_SUPPLIER: "SET_SELECTED_SUPPLIER",

    // 产品和合同项
    SET_AVAILABLE_PRODUCTS: "SET_AVAILABLE_PRODUCTS",
    SET_SELECTED_CONTRACT_ITEMS: "SET_SELECTED_CONTRACT_ITEMS",

    // 事务操作
    SET_TRANSACTIONS: "SET_TRANSACTIONS",
    ADD_TRANSACTION: "ADD_TRANSACTION",
    UPDATE_TRANSACTION: "UPDATE_TRANSACTION",
    ADD_TRANSACTION_SHELLS: "ADD_TRANSACTION_SHELLS",

    // 明细操作（核心）
    ADD_DETAIL: "ADD_DETAIL",
    SET_TRANSACTION_DETAILS: "SET_TRANSACTION_DETAILS",
    UPDATE_DETAIL: "UPDATE_DETAIL",
    DELETE_DETAIL: "DELETE_DETAIL",
    RESTORE_DETAIL: "RESTORE_DETAIL",
    RESET_DETAIL: "RESET_DETAIL",

    // 错误处理
    SET_ERROR: "SET_ERROR",
    CLEAR_ERROR: "CLEAR_ERROR",

    // UI状态
    SET_UI_STATE: "SET_UI_STATE",
};

// 初始状态
export const initialState = {
    // 加载状态
    loading: false,
    dataLoadingStatus: DATA_LOADING_STATUS.IDLE,

    // 基础数据
    selectedFlights: [],
    transactionType: null,
    workflowMode: null, // CREATE | EDIT | MIXED

    // 事务数据
    transactions: [],

    // 机场和供应商
    availableAirports: [],
    activeAirports: [],
    availableSuppliers: [],
    selectedSupplier: null,

    // 产品和合同项
    availableProducts: [],
    selectedContractItems: [],

    // 错误信息
    errors: {},

    // UI状态
    ui: {
        expandedTransactionIds: [],
        selectedTransactionForHistory: null,
    },
};

// 注意：辅助函数已移动到 utils/index.js

// 主 Reducer
export const batchTransactionReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.INITIALIZE_STATE: {
            const { flights, transactionType } = action.payload;
            const airports = extractAirportsFromFlights(flights);

            return {
                ...initialState,
                selectedFlights: flights,
                transactionType,
                availableAirports: airports,
                activeAirports: [], // 初始化时不选中任何机场，用户需要主动选择
                dataLoadingStatus: "IDLE",
                workflowMode: "CREATE", // 默认创建模式，数据加载后可能改变
            };
        }

        case ActionTypes.RESET_STATE: {
            return { ...initialState };
        }

        case ActionTypes.SET_LOADING: {
            return {
                ...state,
                loading: action.payload,
            };
        }

        case ActionTypes.SET_DATA_LOADING_STATUS: {
            return {
                ...state,
                dataLoadingStatus: action.payload,
            };
        }

        case ActionTypes.SET_SELECTED_FLIGHTS: {
            const airports = extractAirportsFromFlights(action.payload);
            return {
                ...state,
                selectedFlights: action.payload,
                availableAirports: airports,
                activeAirports: [], // 重新选择航班时，清空机场选择，用户需要重新选择
            };
        }

        case ActionTypes.SET_TRANSACTION_TYPE: {
            return {
                ...state,
                transactionType: action.payload,
            };
        }

        case ActionTypes.SET_WORKFLOW_MODE: {
            return {
                ...state,
                workflowMode: action.payload,
            };
        }

        case ActionTypes.SET_AVAILABLE_AIRPORTS: {
            return {
                ...state,
                availableAirports: action.payload,
            };
        }

        case ActionTypes.SET_ACTIVE_AIRPORTS: {
            return {
                ...state,
                activeAirports: action.payload,
            };
        }

        case ActionTypes.SET_AVAILABLE_SUPPLIERS: {
            return {
                ...state,
                availableSuppliers: action.payload,
            };
        }

        case ActionTypes.SET_SELECTED_SUPPLIER: {
            return {
                ...state,
                selectedSupplier: action.payload,
            };
        }

        case ActionTypes.SET_AVAILABLE_PRODUCTS: {
            return {
                ...state,
                availableProducts: action.payload,
            };
        }

        case ActionTypes.SET_SELECTED_CONTRACT_ITEMS: {
            return {
                ...state,
                selectedContractItems: action.payload,
            };
        }

        case ActionTypes.SET_TRANSACTIONS: {
            return {
                ...state,
                transactions: action.payload,
            };
        }

        case ActionTypes.ADD_TRANSACTION: {
            return {
                ...state,
                transactions: [...state.transactions, action.payload],
            };
        }

        case ActionTypes.UPDATE_TRANSACTION: {
            const { transactionId, updates } = action.payload;
            return {
                ...state,
                transactions: state.transactions.map((transaction) => {
                    if (transaction.id !== transactionId) return transaction;

                    // 检查是否为新事务（字符串ID表示新事务）
                    const isNewTransaction = typeof transaction.id === 'string';

                    if (isNewTransaction) {
                        // 新事务直接更新，不需要保存原始数据
                        return {
                            ...transaction,
                            ...updates,
                            updatedAt: new Date().toISOString(),
                        };
                    } else {
                        // 已存在事务：如果还没有原始数据，则保存原始数据
                        // 注意：只在第一次修改时保存，避免覆盖已保存的原始数据
                        const originalData = transaction.originalData || {
                            status: transaction.status,
                            remarks: transaction.remarks,
                            // 保存其他需要对比的字段
                        };

                        return {
                            ...transaction,
                            ...updates,
                            originalData: originalData, // 保存原始状态快照
                            updatedAt: new Date().toISOString(),
                        };
                    }
                }),
            };
        }

        case ActionTypes.ADD_TRANSACTION_SHELLS: {
            const { flights } = action.payload;
            const newShells = flights.map(flight => ({
                id: `new_${flight.id}`,
                flightId: flight.id,
                supplierId: null, // 此时可能还未选择供应商
                status: null,
                remarks: null,
                details: [],
                isNewTransaction: true,
                // 补充一些基础信息以供显示
                flightNumber: flight.flight_number,
                flightDate: flight.flight_date,
                route: `${flight.departure_airport}-${flight.arrival_airport}`,
                departureAirport: flight.departure_airport,
                arrivalAirport: flight.arrival_airport,
                aircraftReg: flight.aircraft_reg,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            }));

            return {
                ...state,
                transactions: [...state.transactions, ...newShells],
            };
        }

        // ==================== 明细操作（核心逻辑） ====================

        case ActionTypes.ADD_DETAIL: {
            const { transactionId, detail } = action.payload;

            // 1. 创建新的明细对象
            const newDetail = {
                ...detail,
                id: detail.id || generateTempId(), // 确保新明细有唯一的客户端ID
                clientStatus: CLIENT_STATUS.NEW,
                currentStatus: detail.currentStatus || TRANSACTION_STATUS.PENDING,
                originalData: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            let wasTransactionFound = false;

            // 2. 映射并更新事务列表
            const newTransactions = state.transactions.map((transaction) => {
                // 使用严格相等比较来查找目标事务
                if (transaction.id === transactionId) {
                    wasTransactionFound = true;

                    // 检查产品是否已存在于此事务的明细中
                    const exists = transaction.details.some(
                        (d) => d.contractItemId === newDetail.contractItemId && d.clientStatus !== CLIENT_STATUS.DELETED
                    );

                    if (exists) {
                        console.warn(`跳过添加重复的产品到事务 ${transactionId}:`, newDetail.productName);
                        return transaction; // 如果重复，则不修改此事务
                    }

                    // 3. 如果不重复，则添加新明细并返回更新后的事务
                    return {
                        ...transaction,
                        details: [...transaction.details, newDetail],
                    };
                }
                // 如果不是目标事务，直接返回
                return transaction;
            });

            // 4. [关键调试步骤] 如果遍历完后没有找到任何事务，打印错误日志
            if (!wasTransactionFound) {
                console.error(`[Reducer Bug] ADD_DETAIL: 无法在当前状态中找到 ID 为 "${transactionId}" 的事务。`);
                console.log('当前可用的事务 IDs:', state.transactions.map(t => t.id));
            }

            // 5. 返回更新后的状态
            return {
                ...state,
                transactions: newTransactions,
            };
        }

        case ActionTypes.SET_TRANSACTION_DETAILS: {
            const { transactionId, details } = action.payload;
            // 为每个明细添加必要的字段，类似 ADD_DETAIL 的处理
            const processedDetails = details.map((detail) => ({
                ...detail,
                id: detail.id || generateTempId(),
                clientStatus: CLIENT_STATUS.NEW,
                currentStatus: detail.currentStatus || TRANSACTION_STATUS.PENDING,
                originalData: null, // 新明细没有原始数据
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            }));

            return {
                ...state,
                transactions: state.transactions.map((transaction) => (transaction.id === transactionId ? { ...transaction, details: processedDetails } : transaction)),
            };
        }

        case ActionTypes.UPDATE_DETAIL: {
            const { transactionId, detailId, updates } = action.payload;

            return {
                ...state,
                transactions: state.transactions.map((transaction) => {
                    if (transaction.id !== transactionId) return transaction;

                    return {
                        ...transaction,
                        details: transaction.details.map((detail) => {
                            if (detail.id !== detailId) return detail;

                            // 如果是pristine状态的明细，首次修改时保存原始数据并改为modified状态
                            if (detail.clientStatus === CLIENT_STATUS.PRISTINE) {
                                // 第一次修改pristine状态的明细时，保存原始数据
                                // 注意：pristine状态的明细不应该已经有originalData，所以直接创建
                                const originalData = {
                                    currentStatus: detail.currentStatus, // 明细使用 currentStatus 字段
                                    status: detail.status, // 保留 status 字段以防万一
                                    quantity: detail.quantity,
                                    remarks: detail.remarks,
                                    unitPrice: detail.unitPrice,
                                    currency: detail.currency,
                                    unit: detail.unit,
                                    // 保存其他需要对比的字段
                                };

                                return {
                                    ...detail,
                                    ...updates,
                                    clientStatus: CLIENT_STATUS.MODIFIED,
                                    originalData: originalData, // 保存原始状态快照
                                    updatedAt: new Date().toISOString(),
                                };
                            }

                            // 其他状态的明细直接更新
                            return {
                                ...detail,
                                ...updates,
                                updatedAt: new Date().toISOString(),
                            };
                        }),
                    };
                }),
            };
        }

        case ActionTypes.DELETE_DETAIL: {
            const { transactionId, detailId } = action.payload;

            return {
                ...state,
                transactions: state.transactions.map((transaction) => {
                    if (transaction.id !== transactionId) return transaction;

                    return {
                        ...transaction,
                        details: transaction.details
                            .map((detail) => {
                                if (detail.id !== detailId) return detail;

                                // 如果是新建的明细，直接从数组中移除
                                if (detail.clientStatus === CLIENT_STATUS.NEW) {
                                    return null; // 标记为删除，后面会过滤掉
                                }

                                // 其他状态的明细标记为删除
                                return {
                                    ...detail,
                                    clientStatus: CLIENT_STATUS.DELETED,
                                    updatedAt: new Date().toISOString(),
                                };
                            })
                            .filter((detail) => detail !== null), // 移除新建的明细
                    };
                }),
            };
        }

        case ActionTypes.RESTORE_DETAIL: {
            const { transactionId, detailId } = action.payload;

            return {
                ...state,
                transactions: state.transactions.map((transaction) => {
                    if (transaction.id !== transactionId) return transaction;

                    return {
                        ...transaction,
                        details: transaction.details.map((detail) => {
                            if (detail.id !== detailId) return detail;
                            if (detail.clientStatus !== CLIENT_STATUS.DELETED) return detail;

                            // 恢复删除的明细到之前的状态
                            const previousStatus = detail.originalData ? CLIENT_STATUS.MODIFIED : CLIENT_STATUS.PRISTINE;
                            return {
                                ...detail,
                                clientStatus: previousStatus,
                                updatedAt: new Date().toISOString(),
                            };
                        }),
                    };
                }),
            };
        }

        case ActionTypes.RESET_DETAIL: {
            const { transactionId, detailId } = action.payload;

            return {
                ...state,
                transactions: state.transactions.map((transaction) => {
                    if (transaction.id !== transactionId) return transaction;

                    return {
                        ...transaction,
                        details: transaction.details.map((detail) => {
                            if (detail.id !== detailId) return detail;
                            if (!detail.originalData) return detail;

                            // 重置明细到原始状态
                            return {
                                ...detail.originalData,
                                id: detail.id, // 保持ID不变
                                clientStatus: CLIENT_STATUS.PRISTINE,
                                updatedAt: new Date().toISOString(),
                            };
                        }),
                    };
                }),
            };
        }

        // ==================== 错误处理 ====================

        case ActionTypes.SET_ERROR: {
            const { key, error } = action.payload;
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [key]: error,
                },
            };
        }

        case ActionTypes.CLEAR_ERROR: {
            const { key } = action.payload;
            const newErrors = { ...state.errors };
            delete newErrors[key];
            return {
                ...state,
                errors: newErrors,
            };
        }

        // ==================== UI状态 ====================

        case ActionTypes.SET_UI_STATE: {
            const { key, value } = action.payload;
            return {
                ...state,
                ui: {
                    ...state.ui,
                    [key]: value,
                },
            };
        }

        default:
            return state;
    }
};

// ==================== Action Creators ====================

export const actionCreators = {
    // 初始化和重置
    initializeState: (flights, transactionType) => ({
        type: ActionTypes.INITIALIZE_STATE,
        payload: { flights, transactionType },
    }),

    resetState: () => ({
        type: ActionTypes.RESET_STATE,
    }),

    // 加载状态
    setLoading: (loading) => ({
        type: ActionTypes.SET_LOADING,
        payload: loading,
    }),

    setDataLoadingStatus: (status) => ({
        type: ActionTypes.SET_DATA_LOADING_STATUS,
        payload: status,
    }),

    // 基础数据
    setSelectedFlights: (flights) => ({
        type: ActionTypes.SET_SELECTED_FLIGHTS,
        payload: flights,
    }),

    setTransactionType: (transactionType) => ({
        type: ActionTypes.SET_TRANSACTION_TYPE,
        payload: transactionType,
    }),

    setWorkflowMode: (mode) => ({
        type: ActionTypes.SET_WORKFLOW_MODE,
        payload: mode,
    }),

    // 机场和供应商
    setAvailableAirports: (airports) => ({
        type: ActionTypes.SET_AVAILABLE_AIRPORTS,
        payload: airports,
    }),

    setActiveAirports: (airports) => ({
        type: ActionTypes.SET_ACTIVE_AIRPORTS,
        payload: airports,
    }),

    setAvailableSuppliers: (suppliers) => ({
        type: ActionTypes.SET_AVAILABLE_SUPPLIERS,
        payload: suppliers,
    }),

    setSelectedSupplier: (supplier) => ({
        type: ActionTypes.SET_SELECTED_SUPPLIER,
        payload: supplier,
    }),

    // 产品和合同项
    setAvailableProducts: (products) => ({
        type: ActionTypes.SET_AVAILABLE_PRODUCTS,
        payload: products,
    }),

    setSelectedContractItems: (items) => ({
        type: ActionTypes.SET_SELECTED_CONTRACT_ITEMS,
        payload: items,
    }),

    // 事务操作
    setTransactions: (transactions) => ({
        type: ActionTypes.SET_TRANSACTIONS,
        payload: transactions,
    }),

    addTransaction: (transaction) => ({
        type: ActionTypes.ADD_TRANSACTION,
        payload: transaction,
    }),

    updateTransaction: (transactionId, updates) => ({
        type: ActionTypes.UPDATE_TRANSACTION,
        payload: { transactionId, updates },
    }),

    addTransactionShells: (flights) => ({
        type: ActionTypes.ADD_TRANSACTION_SHELLS,
        payload: { flights },
    }),

    // 明细操作
    addDetail: (transactionId, detail) => ({
        type: ActionTypes.ADD_DETAIL,
        payload: { transactionId, detail },
    }),

    setTransactionDetails: (transactionId, details) => ({
        type: ActionTypes.SET_TRANSACTION_DETAILS,
        payload: { transactionId, details },
    }),

    updateDetail: (transactionId, detailId, updates) => ({
        type: ActionTypes.UPDATE_DETAIL,
        payload: { transactionId, detailId, updates },
    }),

    deleteDetail: (transactionId, detailId) => ({
        type: ActionTypes.DELETE_DETAIL,
        payload: { transactionId, detailId },
    }),

    restoreDetail: (transactionId, detailId) => ({
        type: ActionTypes.RESTORE_DETAIL,
        payload: { transactionId, detailId },
    }),

    resetDetail: (transactionId, detailId) => ({
        type: ActionTypes.RESET_DETAIL,
        payload: { transactionId, detailId },
    }),

    // 错误处理
    setError: (key, error) => ({
        type: ActionTypes.SET_ERROR,
        payload: { key, error },
    }),

    clearError: (key) => ({
        type: ActionTypes.CLEAR_ERROR,
        payload: { key },
    }),

    // UI状态
    setUIState: (key, value) => ({
        type: ActionTypes.SET_UI_STATE,
        payload: { key, value },
    }),
};
